import {
    type SharedSelection,
    DropdownItem,
    DropdownMenu,
    Dropdown as HeroUiDropdown,
    DropdownTrigger,
    DropdownSection,
} from '@heroui/react';
import React, { useCallback, type ComponentProps } from 'react';
import { twMerge } from 'tailwind-merge';
import { type SingleSectionItems, type SectionedItems } from './Dropdown.types';

// General-purpose menu primitive over HeroUI's dropdown: a caller-supplied trigger driving a
// single- or multi-section menu, single or multiple selection, and controlled open/placement.
// For the common single-value form control (a themed button trigger that shows the selected
// option's label + chevron, `string`-in / `string`-out), use the `Select` component, which wraps
// this. Reach for `Dropdown` directly only when you need that broader flexibility.
type Props = {
    children: React.ReactNode;
    selectedKeys: string[];
    selectionMode: ComponentProps<typeof DropdownMenu>['selectionMode'];
    classNames?: {
        dropdown?: ComponentProps<typeof HeroUiDropdown>['classNames'];
        trigger?: ComponentProps<typeof DropdownTrigger>['classNames'];
        menu?: ComponentProps<typeof DropdownMenu>['classNames'];
    };
    onSelectionChange: (selectedKeys: string[]) => void;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    placement?: ComponentProps<typeof HeroUiDropdown>['placement'];
    closeOnSelect?: boolean;
    showArrow?: boolean;
    // Pins the menu to this pixel width rather than its widest item. `Select` passes its measured
    // trigger width, matching what HeroUI's own Select does imperatively.
    menuWidth?: number;
} & (
    | {
          type: 'single-section';
          items: SingleSectionItems;
      }
    | {
          type: 'multi-section';
          items: SectionedItems;
      }
);

function getItemClassNames(itemClassNames?: ComponentProps<typeof DropdownItem>['classNames']) {
    return {
        base: twMerge(
            'text-black text-base',
            'data-[hover=true]:bg-slate-grey-100 data-[focus=true]:bg-slate-grey-100 data-[focus=true]:outline-none',
            'data-[selectable=true]:hover:bg-slate-grey-100 data-[selectable=true]:focus:bg-slate-grey-100',
            itemClassNames?.base,
        ),
        description: itemClassNames?.description,
        title: itemClassNames?.title,
        wrapper: itemClassNames?.wrapper,
        shortcut: itemClassNames?.shortcut,
        selectedIcon: twMerge('relative -top-[2px] *:*:duration-120', itemClassNames?.selectedIcon),
    };
}

// react-aria sets an inline max-height (the space available before the viewport edge) on the wrapper
// it positions, but adds no overflow and doesn't forward the value. Every element down to the
// scrolling content has to relay it, and max-height isn't an inherited property.
const SCROLLABLE_MENU_CHAIN = 'max-h-[inherit]';

// Mirrors `getTransformOrigins` in @heroui/aria-utils, which PopoverContent uses to make the menu
// scale out of the trigger edge. That package is a transitive dependency, so it can't be imported
// here; the table is small and placement-keyed, so a local copy is cheaper than widening
// package.json. Only needed because supplying `motionProps.style` drops HeroUI's own value.
const TRANSFORM_ORIGINS: Record<string, { originX?: number; originY?: number }> = {
    top: { originY: 1 },
    bottom: { originY: 0 },
    left: { originX: 1 },
    right: { originX: 0 },
    'top-start': { originX: 0, originY: 1 },
    'top-end': { originX: 1, originY: 1 },
    'bottom-start': { originX: 0, originY: 0 },
    'bottom-end': { originX: 1, originY: 0 },
    'right-start': { originX: 0, originY: 0 },
    'right-end': { originX: 0, originY: 1 },
    'left-start': { originX: 1, originY: 0 },
    'left-end': { originX: 1, originY: 1 },
};

function transformOriginFor(placement: string) {
    return TRANSFORM_ORIGINS[placement] ?? {};
}

export function Dropdown({
    children,
    items,
    classNames,
    selectedKeys,
    selectionMode,
    onSelectionChange,
    isOpen,
    onOpenChange,
    placement = 'bottom-start',
    closeOnSelect,
    showArrow = false,
    menuWidth,
    type,
}: Props) {
    const handleSelectionChange = useCallback(
        (selection: SharedSelection) => {
            const selected = Array.from(selection);
            onSelectionChange(selected as string[]);
        },
        [onSelectionChange],
    );

    return (
        <HeroUiDropdown
            classNames={{
                base: twMerge(SCROLLABLE_MENU_CHAIN, classNames?.dropdown?.base),
                content: twMerge(
                    'border border-solid border-slate-grey-200 shadow-smallish',
                    // Backstop for a menu sized to a wide item: react-aria's own cross-axis clamp
                    // measures the positioned wrapper, which can be narrower than this content.
                    'max-w-[calc(100vw-2rem)]',
                    SCROLLABLE_MENU_CHAIN,
                    // HeroUI centers this flex column, which makes overflow at the start edge
                    // unreachable (scrollTop can't go negative).
                    'justify-start overflow-y-auto',
                    classNames?.dropdown?.content,
                ),
                trigger: classNames?.dropdown?.trigger,
                backdrop: classNames?.dropdown?.backdrop,
                arrow: classNames?.dropdown?.arrow,
            }}
            // The animated wrapper has no classNames slot but is part of the chain, and is what the
            // content's `w-full` resolves against — so `menuWidth` is applied here.
            motionProps={{
                className: SCROLLABLE_MENU_CHAIN,
                // PopoverContent spreads motionProps over its own `style`, so supplying one
                // replaces its transform origin rather than merging. Carry the origin ourselves.
                ...(menuWidth ? { style: { width: menuWidth, ...transformOriginFor(placement) } } : {}),
            }}
            placement={placement}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            closeOnSelect={closeOnSelect}
            showArrow={showArrow}
        >
            <DropdownTrigger
                classNames={{
                    base: classNames?.trigger?.base,
                    icon: classNames?.trigger?.icon,
                }}
            >
                {children}
            </DropdownTrigger>
            <DropdownMenu
                classNames={{
                    list: twMerge('p-0 m-0', classNames?.menu?.list),
                    base: classNames?.menu?.base,
                    emptyContent: classNames?.menu?.emptyContent,
                }}
                selectedKeys={selectedKeys}
                selectionMode={selectionMode}
                onSelectionChange={handleSelectionChange}
            >
                {type === 'single-section'
                    ? items.map(({ label, classNames: itemClassNames, key, ...itemProps }) => (
                          <DropdownItem key={key} {...itemProps} classNames={getItemClassNames(itemClassNames)}>
                              {label}
                          </DropdownItem>
                      ))
                    : items.map(({ sectionLabel, items: sectionItems }, index) =>
                          sectionItems?.length ? (
                              <DropdownSection
                                  key={sectionLabel}
                                  classNames={{
                                      divider: 'list-none p-0 ms-0 bg-slate-grey-200',
                                      group: 'ps-0',
                                      heading: 'text-slate-grey',
                                  }}
                                  // If the section is the last one, don't show the divider
                                  showDivider={index !== items.length - 1}
                                  title={sectionLabel}
                              >
                                  {sectionItems.map(({ label, classNames: itemClassNames, key, ...itemProps }) => (
                                      <DropdownItem
                                          key={key}
                                          {...itemProps}
                                          classNames={getItemClassNames(itemClassNames)}
                                      >
                                          {label}
                                      </DropdownItem>
                                  ))}
                              </DropdownSection>
                          ) : null,
                      )}
            </DropdownMenu>
        </HeroUiDropdown>
    );
}
